import React, { useEffect, useRef, useState } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import Loading from "../components/Loading";
import { Container } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

import "primeicons/primeicons.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";

const API = "https://api.sandershylandtest.com";

export const UnassignedPaymentsComponent = () => {
  const { user } = useAuth0();
  const history = useHistory();
  const toast = useRef(null);

  const [userFull, setUser] = useState({ _id: "", name: "", admin: false });
  const [payments, setPayments] = useState([]);
  const [projectManagers, setProjectManagers] = useState([]);
  const [selectedLien, setSelectedLien] = useState(null);
  const [selectedPmId, setSelectedPmId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    contractorCompany: { value: null, matchMode: FilterMatchMode.CONTAINS },
    jobNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    invoice: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const formatCurrency = (value) => {
    if (value == null) return "$0.00";
    return Number(value).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getSeverity = (lien) => {
    switch (lien.status) {
      case "started":
      case "unsubmitted":
      case "signed":
        return "primary";
      case "in review":
        return "info";
      case "rejected":
        return "danger";
      case "approved":
        return "warning";
      case "completed":
        return "success";
      default:
        return null;
    }
  };

  const loadData = async () => {
    const currentUser = await fetch(API + "/user/" + user.email, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }).then((response) => response.json());

    setUser(currentUser);

    if (!currentUser.admin) {
      return;
    }

    const [liens, pms] = await Promise.all([
      fetch(API + "/admin/liens/unassigned", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }).then((response) => response.json()),
      fetch(API + "/project-managers/", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }).then((response) => response.json()),
    ]);

    setPayments(Array.isArray(liens) ? liens.reverse() : []);
    setProjectManagers(Array.isArray(pms) ? pms : []);
  };

  useEffect(() => {
    loadData();
  }, []);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let nextFilters = { ...filters };
    nextFilters.global.value = value;
    setFilters(nextFilters);
    setGlobalFilterValue(value);
  };

  const openAssign = (lien) => {
    setSelectedLien(lien);
    setSelectedPmId("");
  };

  const closeAssign = () => {
    setSelectedLien(null);
    setSelectedPmId("");
  };

  const assignPm = async () => {
    if (!selectedLien || !selectedPmId) {
      toast.current.show({
        severity: "warn",
        summary: "Select a PM",
        detail: "Choose a project manager before assigning.",
      });
      return;
    }

    const pm = projectManagers.find((item) => item._id === selectedPmId);
    if (!pm) {
      return;
    }

    setAssigning(true);
    try {
      const result = await fetch(
        API + "/admin/lien/assign-pm/" + selectedLien._id,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            projectManagerId: pm._id,
            projectManager: pm.name,
            updateJob: true,
            notify: true,
          }),
        }
      ).then((response) => response.json());

      if (result.error) {
        throw new Error(result.error);
      }

      toast.current.show({
        severity: "success",
        summary: "Assigned",
        detail:
          "Assigned to " +
          pm.name +
          (result.emailed ? " and notified by email." : "."),
      });
      closeAssign();
      await loadData();
    } catch (error) {
      console.error(error);
      toast.current.show({
        severity: "error",
        summary: "Assignment failed",
        detail: error.message || "Could not assign project manager.",
      });
    } finally {
      setAssigning(false);
    }
  };

  const header = (
    <div className="flex justify-space-between align-items-center flex-wrap gap-2">
      <div>
        <h2 className="text-xl text-900 font-bold m-0">Unassigned Payments</h2>
        <p className="m-0 mt-1" style={{ color: "#555" }}>
          Pay apps with no project manager — these do not appear in PM Liens and
          do not send review emails.
        </p>
      </div>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Keyword Search"
        />
      </span>
    </div>
  );

  if (!userFull._id) {
    return <Loading />;
  }

  if (!userFull.admin) {
    return (
      <Container className="mb-5">
        <h2>Unassigned Payments</h2>
        <p>Admin access is required to view this queue.</p>
      </Container>
    );
  }

  return (
    <Container className="mb-5">
      <Toast ref={toast} />
      <DataTable
        header={header}
        value={payments}
        filters={filters}
        filterDisplay="row"
        sortMode="multiple"
        showGridlines
        stripedRows
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50]}
        tableStyle={{ minWidth: "50rem" }}
        globalFilterFields={[
          "invoice",
          "value",
          "contractorCompany",
          "contractor",
          "status",
          "jobNumber",
        ]}
        emptyMessage="No unassigned payments found."
      >
        <Column
          field="date"
          header="Date"
          sortable
          body={(row) => formatDate(row.date)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="value"
          header="Value"
          sortable
          body={(row) => formatCurrency(row.value)}
        />
        <Column field="jobNumber" header="Job Number" sortable />
        <Column field="invoice" header="Invoice" sortable />
        <Column field="contractorCompany" header="Contractor" sortable />
        <Column
          field="status"
          header="Status"
          sortable
          body={(lien) => (
            <Tag value={lien.status} severity={getSeverity(lien)} />
          )}
        />
        <Column
          body={(lien) => (
            <div className="button_flex">
              <Button
                icon="pi pi-user-plus"
                raised
                severity="warning"
                aria-label="Assign PM"
                onClick={() => openAssign(lien)}
                tooltip="Assign Project Manager"
              />
              <Button
                icon="pi pi-eye"
                outlined
                raised
                severity="info"
                aria-label="View"
                onClick={() => history.push("/approve/" + lien._id)}
                tooltip="Open approval"
              />
              {lien.attachment_url ? (
                <a
                  className="attachment active"
                  href={
                    "https://sanders-hyland.s3.amazonaws.com/uploads/" +
                    lien.attachment_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Attachment
                </a>
              ) : null}
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header="Assign Project Manager"
        visible={!!selectedLien}
        style={{ width: "32rem" }}
        modal
        onHide={closeAssign}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={closeAssign}
              disabled={assigning}
            />
            <Button
              label={assigning ? "Assigning..." : "Assign & Notify"}
              severity="success"
              onClick={assignPm}
              disabled={assigning || !selectedPmId}
            />
          </div>
        }
      >
        {selectedLien ? (
          <div>
            <p>
              <strong>Invoice:</strong> #{selectedLien.invoice}
              <br />
              <strong>Job:</strong> {selectedLien.jobNumber}
              <br />
              <strong>Contractor:</strong> {selectedLien.contractorCompany}
            </p>
            <label htmlFor="pm-select" style={{ display: "block", marginBottom: 8 }}>
              Project Manager
            </label>
            <Dropdown
              id="pm-select"
              value={selectedPmId}
              options={projectManagers.map((pm) => ({
                label: pm.name + (pm.email ? " (" + pm.email + ")" : ""),
                value: pm._id,
              }))}
              onChange={(e) => setSelectedPmId(e.value)}
              placeholder="Select a project manager"
              className="w-full"
              style={{ width: "100%" }}
            />
            <p className="mt-3 mb-0" style={{ color: "#555", fontSize: "0.9rem" }}>
              This assigns the PM on the payment, adds them to the job if needed,
              and emails them a review link.
            </p>
          </div>
        ) : null}
      </Dialog>
    </Container>
  );
};

export default withAuthenticationRequired(UnassignedPaymentsComponent, {
  onRedirecting: () => <Loading />,
});
